'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { HoloCard } from './HoloCard';
import { StatsRow } from './StatsRow';
import { InfoCard } from './InfoCard';
import { StatusPill } from './StatusPill';
import { ScanHistoryModal } from './ScanHistoryModal';
import { ExperienceCard } from './ExperienceCard';
import { DocumentsCard } from './DocumentsCard';
import { GoogleReviewCard } from './GoogleReviewCard';
import { PRODUCT_FALLBACK, type VaultEntry } from '@/lib/vault';

interface RevisitStateProps {
  code: string;
  entry: VaultEntry;
  scanId?: string | null;
}

export function RevisitState({ code, entry, scanId }: RevisitStateProps) {
  const t = useTranslations('public');
  const [scanHistoryOpen, setScanHistoryOpen] = useState(false);

  const serial = entry.serialNumber != null ? String(entry.serialNumber).padStart(7, '0') : null;
  const productName = entry.productName || PRODUCT_FALLBACK;

  const firstSeenFormatted = new Date(entry.firstSeenAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const lastSeenFormatted = new Date(entry.lastSeenAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const productionDateFormatted = entry.productionDate
    ? new Date(entry.productionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const useByDateFormatted = entry.useByDate
    ? new Date(entry.useByDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="flex flex-col gap-3 px-4" style={{ paddingBottom: 100 }}>
      {/* Holographic certificate — flips in from scanning back-face */}
      <HoloCard serial={entry.serialNumber} flipIn variant="blue" title={t('revisit.title')} productName={productName} />

      {/* Stats — blue accent, scan count tappable */}
      <div className="pub-reveal pub-reveal-2">
        <StatsRow
          items={[
            { value: entry.scanCount, label: t('verified.totalScans'), color: 'var(--pub-blue)', onPress: () => setScanHistoryOpen(true) },
            { value: firstSeenFormatted, label: t('verified.firstSeen') },
            { value: t('verified.clean'), label: t('verified.history'), color: 'var(--pub-green)' },
          ]}
        />
      </div>

      {/* Leave a Google review — kept above the fold, in the compliance card's spot */}
      <div className="pub-reveal pub-reveal-3">
        <GoogleReviewCard />
      </div>

      {/* Manufacturing & Traceability data */}
      <div className="pub-reveal pub-reveal-3">
        <InfoCard
          rows={[
            ...(serial ? [{ label: t('compliance.serialNumber'), value: serial, mono: true }] : []),
            ...(entry.batchName ? [{ label: t('verified.batch'), value: entry.batchName, mono: true }] : []),
            ...(productionDateFormatted ? [{ label: t('compliance.manufactured'), value: productionDateFormatted }] : []),
            ...(useByDateFormatted ? [{ label: t('compliance.useBy'), value: useByDateFormatted }] : []),
            ...(entry.manufacturingLot ? [{ label: t('compliance.lot'), value: entry.manufacturingLot, mono: true }] : []),
            ...(entry.productionLine ? [{ label: t('compliance.productionLine'), value: entry.productionLine, mono: true }] : []),
            ...(entry.qualityCheck !== undefined ? [{
              label: t('compliance.qualityCheck'),
              value: <StatusPill variant={entry.qualityCheck ? 'green' : 'red'}>
                {entry.qualityCheck ? t('compliance.passed') : t('compliance.failed')}
              </StatusPill>,
            }] : []),
            ...(entry.operatorName ? [{
              label: t('compliance.operator'),
              value: `${entry.operatorName}${entry.operatorDepartment ? ` · ${entry.operatorDepartment}` : ''}`,
            }] : []),
            ...(entry.clientName ? [{ label: t('compliance.brand'), value: entry.clientName }] : []),
            { label: t('preview.lastSeen'), value: lastSeenFormatted },
            {
              label: t('verified.ownership'),
              value: <StatusPill variant="blue">{t('revisit.thisDevice')}</StatusPill>,
            },
          ]}
        />
      </div>

      {/* Eclipse Experience */}
      {scanId && (
        <ExperienceCard scanId={scanId} code={code} />
      )}

      {/* Document Access */}
      <div className="pub-reveal pub-reveal-4">
        <DocumentsCard />
      </div>

      {/* CTA */}
      <div className="pub-reveal pub-reveal-4">
        <a
          href={`/contact?reason=inquiry&code=${encodeURIComponent(code)}`}
          className="pub-btn pub-btn-primary"
          style={{ background: '#000', color: '#fff', textAlign: 'center', textDecoration: 'none', display: 'block' }}
        >
          {t('verified.contactUs')}
        </a>
      </div>

      {/* Scan history modal */}
      <ScanHistoryModal
        open={scanHistoryOpen}
        onClose={() => setScanHistoryOpen(false)}
        code={code}
      />
    </div>
  );
}
